import { useMemo } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { ProtectedRoute } from '@/components/protected-route'
import { ErrorPage } from '@shared/components/error-page/error-page'
import { HomePage } from '@/pages/home/home.page'
import { PageNotFound } from '@/pages/page-not-found.page'
import { StationContextProvider } from '@/pages/station-context'
import { CreateWoolSpecificationPage } from '@/pages/wool-specifications/create-wool-specification/create-wool-specification.page'
import { WoolSpecificationsPage } from '@/pages/wool-specifications/wool-specifications.page'
import { LoggedOutPage } from '@/pages/logged-out.page'
import { ViewWoolSpecificationPage } from '@/pages/wool-specifications/view-wool-specification/view-wool-specification.page'
import { WoolDataTabsPage } from '@/pages/wool-data/wool-data-tabs.page'
import { OffersListPage } from '@/pages/offers/offers-list.page'
import { StationHeader } from '@/components/stations/station-header/station-header'
import { routes } from '@/routing/routes'
import { AppShellWrapper } from '@/components/app-shell-wrapper'
import { SpeciAction } from '@shared/components/speci/speci-editor/speci-editor'
import { ResourcesPage } from '@/pages/resources/resources.page'
import { ViewOfferPage } from '@/pages/offers/view-offer/view-offer.page'
import { PageTracker } from '@/routing/page-tracker'

export const routerConfig = [
  {
    element: (
      <ErrorBoundary fallbackRender={ErrorPage}>
        <PageTracker />
        <ProtectedRoute />
      </ErrorBoundary>
    ),
    children: [
      {
        path: routes.home.path,
        element: (
          <StationContextProvider>
            <AppShellWrapper />
          </StationContextProvider>
        ),
        children: [
          {
            path: routes.home.path,
            element: <HomePage />,
          },
          {
            path: routes.resources.path,
            element: <ResourcesPage />,
          },
          {
            path: routes.stationsModule.path,
            element: <StationHeader />,
            children: [
              {
                path: routes.listSpeci.path,
                element: <WoolSpecificationsPage />,
              },
              {
                path: routes.newSpeci.path,
                element: <CreateWoolSpecificationPage action={SpeciAction.NEW} />,
              },
              {
                path: routes.editSpeci.path,
                element: <CreateWoolSpecificationPage action={SpeciAction.EDIT} />,
              },
              {
                path: routes.viewSpeci.path,
                element: <ViewWoolSpecificationPage />,
              },
              {
                path: routes.woolData.path,
                element: <WoolDataTabsPage />,
              },
              {
                path: routes.woolDataTab.path,
                element: <WoolDataTabsPage />,
              },
              {
                path: routes.listOffers.path,
                element: <OffersListPage />,
              },
              {
                path: routes.viewOffer.path,
                element: <ViewOfferPage />,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    children: [
      {
        path: '/logged-out',
        element: <LoggedOutPage />,
      },
      {
        path: '*',
        element: <PageNotFound />,
      },
    ],
  },
]

export function Router() {
  const router = useMemo(
    () =>
      createBrowserRouter(routerConfig, {
        future: {
          v7_relativeSplatPath: true,
          v7_fetcherPersist: true,
          v7_normalizeFormMethod: true,
          v7_partialHydration: true,
          v7_skipActionErrorRevalidation: true,
        },
      }),
    []
  )

  const routerProvider = useMemo(
    () => (
      <RouterProvider
        router={router}
        future={{
          v7_startTransition: true,
        }}
      />
    ),
    [router]
  )

  return routerProvider
}
